/**
 * The ring drawn on the grass under a player, to show which one you are driving.
 *
 * Nothing in the original marks the selected player, which does not survive contact with any
 * camera a football game would use: twenty-two men of identical build, tens of metres away, and
 * no way to tell which one answers your stick. So the web version draws one, and a second one in
 * another colour under whoever is on the ball when that is somebody else. It is a setting in the
 * visual options and can be switched off there.
 *
 * ## It is paint on the grass, not an object lying on it
 *
 * The ring is drawn as a decal: it blends its own colour into the pitch's albedo in the
 * G-buffer and writes nothing else. It contributes no depth, no surface normal, no shininess
 * and no self-illumination, so every later pass — sunlight, shadow, ambient occlusion, the
 * edge-detecting anti-aliasing, the distance haze — treats those pixels as the grass they
 * still are. The ring is simply grass of a different colour.
 *
 * That is what stops it reading as a hole cut in the pitch, and it is worth spelling out what
 * it buys:
 *
 *   - The edge can be genuinely soft. An opaque draw into a G-buffer can only alpha-test, so
 *     its edge is all-or-nothing however the texture is authored; blending gives a gradient.
 *   - Walk into a shadow and the ring dims with the grass around it, because it is lit by the
 *     same sunlight term. Its contrast against the grass is preserved either way, which is the
 *     part that matters for finding your man.
 *   - It cannot z-fight, cannot make the anti-aliasing fire along a false silhouette, and
 *     cannot cast or catch an occlusion term of its own.
 *
 * The blend is arranged so that only the colour channels of the albedo target move. Every
 * alpha channel in the G-buffer carries a separate material property — specular amount,
 * shininess, self-illumination — and all three are left exactly as the pitch wrote them.
 *
 * ## The shape is computed, not sampled
 *
 * There is no texture. The ring is worked out in the fragment shader from the fragment's
 * distance to the marker's centre, measured against `fwidth`, which is how many metres of
 * pitch one screen pixel covers there. So the ring holds a constant width *on screen*: it
 * neither turns into a fat black band up close nor thins away to nothing when the camera pulls
 * back to show the stadium, and it is smooth all the way round the ellipse even though
 * perspective squashes the far side.
 *
 * A textured ring cannot do that. It also has a failure mode this avoids: the previous version
 * drew the ring into a 128-pixel image and let the hardware build the reduced-size copies, and
 * at match distance the ring arrived on screen pure black — the darkest thing in the frame,
 * with a hard edge, on lit grass. Holding the colour constant in every texel was supposed to
 * fix that and did not. Whatever the mechanism, a shader that never samples anything cannot
 * lose its colour that way.
 *
 * It is put on its own render layer so that the shadow pass skips it — a shadow of a ring
 * lying on the grass would be a crescent of darkness peeking out from under itself.
 */

import {
  AddEquation,
  BufferAttribute,
  BufferGeometry,
  CustomBlending,
  DoubleSide,
  GLSL3,
  Mesh,
  OneFactor,
  OneMinusSrcAlphaFactor,
  RawShaderMaterial,
  SrcAlphaFactor,
  Vector3,
  Vector4,
  ZeroFactor,
} from 'three';

/** The camera sees this layer; the shadow cameras, left on the default layer, do not. */
export const MARKER_LAYER = 1;

/**
 * Every number the ring's shape is built from. Distances are metres of pitch unless the name
 * says pixels. The shader is handed these as uniforms and `ringCoverage` below models the same
 * arithmetic, so a test can hold both to the same account.
 */
export const MARKER_SHAPE = {
  /** Half the width of the quad the ring is drawn on. Must clear `maxReach()` below. */
  quadHalfSize: 0.95,
  /** Metres above the grass. The ring writes no depth, so this only has to win the depth test. */
  lift: 0.03,
  /** Where the ring sits. Just over a metre across — a footballer's patch of grass. */
  ringRadius: 0.52,
  /** Half the ring's thickness. Nine centimetres of line, against a pitch marking's twelve. */
  bandHalfWidth: 0.045,
  /** How far the ring fades out over, at close range. */
  edgeSoftness: 0.022,
  /** The ring never gets thinner than this many pixels each side of its centre line. */
  minBandPixels: 0.75,
  /** Nor does its fade get tighter than this many pixels, which is what keeps it smooth. */
  minSoftPixels: 1.0,
  /**
   * The widest a pixel is allowed to count as, in metres of pitch.
   *
   * Holding the ring to a minimum width in pixels means it grows in metres as the camera
   * retreats, and unchecked it would eventually grow past the edge of the quad it is drawn
   * on and be cut off square. This stops that, at the price of the ring finally beginning to
   * thin once a pixel covers more than this. That happens when the whole pitch is about five
   * hundred pixels wide, well past any framing a football camera would use, and by then the
   * ring is six pixels across and the player under it is smaller than that.
   */
  pixelCeiling: 0.2,
  /** How far the glow either side of the ring reaches. */
  haloWidth: 0.16,
  /** Strongest tint the ring itself applies. Short of 1 so a little grass shows through it. */
  ringStrength: 0.9,
  /** Strongest tint the glow applies. Barely there on its own; it is what softens the ring. */
  haloStrength: 0.16,
  /** Below this the fragment is thrown away rather than blended for nothing. */
  minCoverage: 0.004,
} as const;

/**
 * The furthest from the marker's centre anything can ever be tinted, in metres.
 *
 * The quad has to be wider than this or the ring runs off its edge and gains a straight side.
 */
export function maxReach(): number {
  const s = MARKER_SHAPE;
  const band = Math.max(s.bandHalfWidth, s.pixelCeiling * s.minBandPixels);
  const soft = Math.max(s.edgeSoftness, s.pixelCeiling * s.minSoftPixels);
  return s.ringRadius + Math.max(band + soft, s.haloWidth);
}

/**
 * How much of the asked-for colour actually reaches the grass.
 *
 * The caller names a colour; this decides how bright it is, and treats it as paint rather than
 * as a light. Handed straight through, a saturated colour renders with two of its three
 * channels clipped — a white-hot ring that dominates the frame just as the black one did, only
 * the other way up. At this reflectance the marker lands about four times the brightness of the
 * grass on both the light and the dark mown stripes: unmistakable, but not the brightest thing
 * on the pitch. That is still true of the ring seen through a wide camera's haze and of one
 * standing in shadow.
 */
export const PAINT_REFLECTANCE = 0.55;

function srgbChannelToLinear(byte: number): number {
  const c = byte / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/**
 * A `#rrggbb` colour as the linear albedo the shader wants.
 *
 * Linear, and dimmed to `PAINT_REFLECTANCE`. The shaders are supplied whole rather than
 * assembled by three.js, so nothing decodes an sRGB value on the way in — a colour handed over
 * undecoded would be roughly twice as bright as asked for, on top of being too bright already.
 */
export function paintColor(color: string): Vector3 {
  const hex = Number.parseInt(color.replace('#', ''), 16);
  return new Vector3(
    srgbChannelToLinear((hex >> 16) & 0xff),
    srgbChannelToLinear((hex >> 8) & 0xff),
    srgbChannelToLinear(hex & 0xff),
  ).multiplyScalar(PAINT_REFLECTANCE);
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(Math.max((x - edge0) / (edge1 - edge0), 0), 1);
  return t * t * (3 - 2 * t);
}

/**
 * How strongly the marker tints the grass, `radius` metres out from its centre, when one
 * screen pixel covers `pixel` metres of pitch there.
 *
 * This is the same arithmetic as the fragment shader's, kept here in a form a test can read.
 * The two are written to match line for line; change one and change the other.
 */
export function ringCoverage(radius: number, pixel: number): number {
  const s = MARKER_SHAPE;
  const p = Math.min(pixel, s.pixelCeiling);
  const band = Math.max(s.bandHalfWidth, p * s.minBandPixels);
  const soft = Math.max(s.edgeSoftness, p * s.minSoftPixels);
  const offRing = Math.abs(radius - s.ringRadius);
  const ring = 1 - smoothstep(band - soft, band + soft, offRing);
  const halo = 1 - smoothstep(0, s.haloWidth, offRing);
  const cover = Math.max(ring * s.ringStrength, halo * halo * s.haloStrength);
  return Math.min(Math.max(cover, 0), 1);
}

/**
 * A flat quad in the simulation's XY plane, carrying nothing but position: the shader works
 * the rest out from where the fragment falls on it. Three.js's own PlaneGeometry would do,
 * but it also ships texture coordinates and a normal that this has no use for.
 */
function quadGeometry(): BufferGeometry {
  const geometry = new BufferGeometry();
  const r = MARKER_SHAPE.quadHalfSize;
  // prettier-ignore
  const positions = [-r, -r, 0,  r, -r, 0,  r, r, 0,  -r, r, 0];
  geometry.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3));
  geometry.setIndex([0, 1, 2, 0, 2, 3]);
  return geometry;
}

const VERTEX_SHADER = `
precision highp float;

uniform mat4 modelMatrix;
uniform mat4 viewMatrix;
uniform mat4 projectionMatrix;

layout(location = 0) in vec4 position;

// Where on the quad this fragment falls, in metres from the marker's centre.
out vec2 groundOffset;
// The quad's own normal, in world space. Never read while the blend below is in force —
// the pitch's normal is the one that survives — but written so that a driver that somehow
// declined to blend would leave a sane surface behind rather than a zero-length normal,
// which the lighting pass normalises and turns into a black hole.
out vec3 worldNormal;

void main(void) {
  groundOffset = position.xy;
  worldNormal = normalize(mat3(modelMatrix) * vec3(0.0, 0.0, 1.0));
  gl_Position = projectionMatrix * (viewMatrix * (modelMatrix * position));
}
`;

const FRAGMENT_SHADER = `
precision highp float;

// Linear albedo of the paint.
uniform vec3 paintColor;
// x = ring radius, y = band half-width, z = edge softness, w = halo width. All metres.
uniform vec4 ringShape;
// x = minimum band half-width in pixels, y = minimum softness in pixels,
// z = ring strength, w = halo strength.
uniform vec4 ringLimits;
// The widest a pixel may count as, in metres, so the ring cannot outgrow the quad.
uniform float pixelCeiling;
// Coverage below which the fragment is thrown away.
uniform float minCoverage;

in vec2 groundOffset;
in vec3 worldNormal;

// The G-buffer contract, as simple.frag writes it.
layout(location = 0) out vec4 out_albedo;
layout(location = 1) out vec4 out_normal;
layout(location = 2) out vec4 out_aux;

void main(void) {
  float radius = length(groundOffset);
  // Metres of pitch under one screen pixel, measured along the radius. Every width below is
  // held against it, so the ring keeps its size on screen however far off the camera stands,
  // and stays even all the way round an ellipse that perspective has squashed.
  float pixel = min(fwidth(radius), pixelCeiling);

  float band = max(ringShape.y, pixel * ringLimits.x);
  float soft = max(ringShape.z, pixel * ringLimits.y);
  float offRing = abs(radius - ringShape.x);

  float ring = 1.0 - smoothstep(band - soft, band + soft, offRing);
  float halo = 1.0 - smoothstep(0.0, ringShape.w, offRing);
  float cover = clamp(max(ring * ringLimits.z, halo * halo * ringLimits.w), 0.0, 1.0);
  if (cover < minCoverage) discard;

  out_albedo = vec4(paintColor, cover);
  // Zero in the alpha of both, which is the blend factor for that target: the pitch's own
  // normal, shininess, specular amount and self-illumination all pass through untouched.
  // Only the albedo's colour changes.
  out_normal = vec4(worldNormal, 0.0);
  out_aux = vec4(0.0);
}
`;

function markerMaterial(color: string): RawShaderMaterial {
  const s = MARKER_SHAPE;
  const material = new RawShaderMaterial({
    vertexShader: VERTEX_SHADER,
    fragmentShader: FRAGMENT_SHADER,
    glslVersion: GLSL3,
    uniforms: {
      paintColor: { value: paintColor(color) },
      ringShape: {
        value: new Vector4(s.ringRadius, s.bandHalfWidth, s.edgeSoftness, s.haloWidth),
      },
      ringLimits: {
        value: new Vector4(s.minBandPixels, s.minSoftPixels, s.ringStrength, s.haloStrength),
      },
      pixelCeiling: { value: s.pixelCeiling },
      minCoverage: { value: s.minCoverage },
    },
  });

  // Tint the albedo, touch nothing else.
  //
  // The colour factors blend each target by that target's OWN output alpha, so writing zero
  // alpha into the normal and aux targets leaves them exactly as the pitch left them. The
  // alpha factors are zero-source, one-destination, which spares the alpha channels the same
  // way: they carry specular amount, shininess and self-illumination, none of which a decal
  // has any business rewriting.
  //
  // Not three's AlphaBlending preset, which would leave the alpha channels blending too.
  material.transparent = true;
  material.blending = CustomBlending;
  material.blendEquation = AddEquation;
  material.blendSrc = SrcAlphaFactor;
  material.blendDst = OneMinusSrcAlphaFactor;
  material.blendEquationAlpha = AddEquation;
  material.blendSrcAlpha = ZeroFactor;
  material.blendDstAlpha = OneFactor;

  // Depth-tested so a player's boot, or the ball, hides the part of the ring behind it; no
  // depth written, so the fragment the ring tints keeps the grass's distance and every later
  // pass reads the pitch, not a surface floating three centimetres over it.
  material.depthTest = true;
  material.depthWrite = false;
  // Two triangles: culling nothing costs nothing, and removes the one way a hand-wound quad
  // can vanish entirely. The single-pass flag is not optional with it — three.js draws a
  // double-sided see-through material twice, back faces then front, and this one would then
  // blend its colour onto the grass twice and come out far stronger than it was tuned to be.
  material.side = DoubleSide;
  material.forceSinglePass = true;
  return material;
}

export class PlayerMarker {
  readonly mesh: Mesh;

  constructor(color: string) {
    this.mesh = new Mesh(quadGeometry(), markerMaterial(color));
    this.mesh.layers.set(MARKER_LAYER);
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
  }

  /** Place it under a player, given his position in simulation metres. */
  show(position: Vector3): void {
    this.mesh.position.set(position.x, position.y, MARKER_SHAPE.lift);
    this.mesh.visible = true;
  }

  hide(): void {
    this.mesh.visible = false;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as RawShaderMaterial).dispose();
  }
}
